/**
 * Module Access Middleware
 * 
 * This middleware checks if the user has access to specific modules.
 * To use, add to definePageMeta in a page:
 * 
 * definePageMeta({
 *   middleware: ['module-access'],
 *   requiredModules: ['presentation', 'home'] // at least one of these
 * })
 * 
 * Admin and SuperAdmin roles bypass all module checks.
 */

import { useAuthService } from '~/composables/useAuthService'

export default defineNuxtRouteMiddleware((to) => {
    // Skip if no module requirements defined
    const requiredModules = to.meta.requiredModules as string[] | undefined
    if (!requiredModules || requiredModules.length === 0) {
        return
    }

    const { currentUser } = useAuthService()
    const user = currentUser.value

    // Not authenticated - auth.global.ts will handle redirect
    if (!user) {
        return
    }

    // Admin and SuperAdmin always have access
    if (user.roles.includes('SuperAdmin') || user.roles.includes('Admin')) {
        return
    }

    // Check if user has 'all' modules access
    if (user.modules?.includes('all')) {
        return
    }

    // Check if user has at least one of the required modules
    const hasRequiredModule = requiredModules.some(mod => user.modules?.includes(mod))

    if (!hasRequiredModule) {
        return navigateTo('/access-denied')
    }
})
